import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  GlobalStyles,
  IconButton,
  Stack,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, MoreVert } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import { AppBar } from 'components/AppBar/AppBar';
import { NoResources } from 'components/NoResources';
import { KyronMenu } from 'components/KyronMenu';
import { useFeatures } from 'components/FeaturesContext';
import { useLessonQuery, useLessonSectionsQuery } from 'controllers/react-query';
import { LessonSection } from 'controllers/types';
import { PublishStudioLessonButton } from './components/PublishStudioLessonButton';
import { SectionDetail } from './components/SectionDetail';
import { SectionList } from './components/SectionList';
import { LessonNameField } from './components/LessonNameField';
import { MisconceptionDialog } from './components/MisconceptionDialog';
import { SectionListEditor } from './components/SectionListEditor';

function previewLessonLink(lessonId: string) {
  return `/video_player/${lessonId}?restart=true&force_separate=true`;
}

type OldSectionListEditorProps = {
  lessonSections: LessonSection[];
  isLoading: boolean;
};
const OldSectionListEditor = ({ lessonSections, isLoading }: OldSectionListEditorProps) => {
  const isMobileView = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const currentSectionId = lessonSections[selectedIdx]?.id;
  const isFinalSectionSelected = selectedIdx === lessonSections.length - 1;

  const sectionDetail = isFinalSectionSelected ? (
    <SectionDetail currentSectionId={currentSectionId} isFixedPosition />
  ) : (
    <SectionDetail currentSectionId={currentSectionId} />
  );

  return (
    <Stack direction={isMobileView ? 'column' : 'row'} gap={3}>
      <SectionList
        selectedIdx={selectedIdx}
        selectIdx={setSelectedIdx}
        sx={{ width: isMobileView ? '100%' : 280 }}
        lessonSections={lessonSections}
        isLoading={isLoading}
      />

      {isMobileView ? <Collapse in={selectedIdx !== null}>{sectionDetail}</Collapse> : sectionDetail}
    </Stack>
  );
};

// export for testing
export function useLessonAndSectionsQuery(lessonId: number) {
  const { error: lessonError, isFetching: isLoadingLesson, data: lesson } = useLessonQuery(lessonId);
  const {
    error: sectionError,
    isFetching: isLoadingSections,
    data: lessonSectionsData,
  } = useLessonSectionsQuery(lessonId);
  const { lesson_sections: lessonSections = [] } = lessonSectionsData || {};
  const isLoading = isLoadingLesson || isLoadingSections;
  const errors = [lessonError, sectionError].filter((e): e is Error => !!e);

  return { isLoading, errors, lesson, lessonSections };
}

export function Editor() {
  const isMobileView = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const { use_russet_editor: useRussetEditor } = useFeatures(); // TODO: remove useRussetEditor once new editor is feature-ready

  const { lessonId } = useParams<{ lessonId: string }>();
  const { lesson, lessonSections, errors, isLoading } = useLessonAndSectionsQuery(Number(lessonId));
  const [openMisconceptionDialog, setOpenMisconceptionDialog] = useState(false);
  const hasMisconceptions = Boolean(lesson?.has_misconceptions);
  const backgroundTasks =
    lesson?.background_tasks?.filter(task => task.status === 'started' || task.status === null) || [];
  const hasRunningBackgroundTasks = backgroundTasks.length > 0;

  const isCreatingSections = lessonSections.length === 0 && hasRunningBackgroundTasks;

  if (!lessonId) return <NoResources message='No lesson selected' />;

  const menuActions = (
    <>
      {hasMisconceptions && (
        <Button
          onClick={() => setOpenMisconceptionDialog(true)}
          variant='outlined'
          disabled={hasRunningBackgroundTasks}
        >
          Misconceptions
        </Button>
      )}
      <Button to={previewLessonLink(lessonId)} component={Link} variant='outlined' disabled={hasRunningBackgroundTasks}>
        Preview Lesson
      </Button>
      <PublishStudioLessonButton lessonId={Number(lessonId)} disabled={hasRunningBackgroundTasks} />
    </>
  );

  const lessonNameField = lesson ? <LessonNameField lesson={lesson} /> : 'Loading...';

  return (
    <>
      <GlobalStyles styles={theme => ({ body: { backgroundColor: theme.palette.background.paper } })} />

      <AppBar
        title={!isMobileView ? lessonNameField : null}
        navButton={
          <IconButton to='/studio/library' component={Link}>
            <ArrowBack />
          </IconButton>
        }
        actions={
          isMobileView ? (
            <KyronMenu menuIcon={<MoreVert />}>
              <Stack m={1} gap={1}>
                {menuActions}
              </Stack>
            </KyronMenu>
          ) : (
            menuActions
          )
        }
        sx={{ bgcolor: 'background.paper' }}
      />

      {isMobileView ? <Box m={1}>{lessonNameField}</Box> : null}

      {hasRunningBackgroundTasks ? (
        <Alert severity='info' sx={{ ml: 2, mr: 2 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
            Your lesson is being created. Publication and other actions will be available when processing completes.
            <CircularProgress aria-label='progress-bar-loading' size={20} />
          </Stack>
        </Alert>
      ) : null}

      {errors.length > 0 ? (
        <Stack gap={1} ml={2} mr={2}>
          {errors.map(error => (
            <Alert key={error.message} severity='error'>
              {error.message}
            </Alert>
          ))}
        </Stack>
      ) : null}

      {/* TODO(derick): remove useRussetEditor once new editor is feature-ready (may also want to move SectionListEditor into this component) */}
      {useRussetEditor ? (
        <SectionListEditor lessonSections={lessonSections} />
      ) : (
        <OldSectionListEditor isLoading={isLoading || isCreatingSections} lessonSections={lessonSections} />
      )}

      <Dialog open={openMisconceptionDialog} onClose={() => setOpenMisconceptionDialog(false)} maxWidth='md'>
        <MisconceptionDialog lessonId={Number(lessonId)} closeDialog={() => setOpenMisconceptionDialog(false)} />
      </Dialog>
    </>
  );
}
